<template>
  <div class="sh-messaging-beta-banner">
    <span class="sh-beta-banner-icon">
      <i class="fal fa-party-horn" aria-hidden="true"></i>
    </span>
    <div class="sh-beta-banner-text">
      <span class="sh-beta-banner-title">Group Messaging is now in Beta!</span>
      <span class="sh-beta-banner-subtitle">Please help us to continue improving this feature with feedback and suggestions.</span>
    </div>
    <a class="sh-beta-banner-btn" href="mailto:support@rallyhood.com?subject=Group%20Messaging%20Feedback">
      Contact Support
    </a>
  </div>
  <div :class="['sh-messaging-page-container', { 'is-thread-open': !!activeThreadId, 'is-list-collapsed': !subchannelsState }]">
    <aside class="sh-messaging-sidebar">
      <div class="sh-messaging-sidebar-header">
        <button class="sh-new-message-btn" type="button" @click="onNewMessage">
          <i class="fal fa-message-medical" aria-hidden="true"></i>
          <span class="sh-new-message-label">New Message</span>
        </button>
      </div>

      <div class="sh-messaging-sidebar-list">
        <SubchannelsMenu
          v-on:perform="perform"
          :threads="threads"
          :active="activeThreadId"
        />
      </div>

      <div class="sh-messaging-sidebar-footer">
        <a class="sh-hide-list" href="#" @click.prevent="toggleSubchannels">
          <i :class="['fal', subchannelsState ? 'fa-chevron-left' : 'fa-chevron-right']" aria-hidden="true"></i>
          <span v-if="subchannelsState" class="sh-hide-list-label">Hide List</span>
        </a>
        <button
          v-if="subchannelsState"
          type="button"
          class="sh-view-blocked-btn"
          @click="viewBlockedUsers"
        >
          View Blocked Users
        </button>
      </div>
    </aside>

    <NewMessageComposer
      v-if="composerOpen"
      @cancel="composerOpen = false"
      @created="onThreadCreated"
    />

    <section v-else class="sh-messaging-main">
      <header
        v-if="activeThread"
        class="sh-messaging-header"
      >
        <span class="sh-messaging-back" aria-label="Back to conversations" @click="backToList">
          <i class="fal fa-chevron-left" aria-hidden="true"></i>
        </span>
        <div :class="['sh-messaging-header-avatars', 'sh-conv-row-avatar', { 'sh-avatar-stack': activeThread.isGroup }]">
          <UserAvatar
            v-for="uid in headerAvatarUserIds"
            :key="uid"
            :pUserId="uid"
            :pSize="activeThread.isGroup ? '20px' : '32px'"
            :p-line-height="activeThread.isGroup ? '20px' : '2rem'"
            :p-font-size="activeThread.isGroup ? '0.6rem' : '1rem'"
            type="author-avatar"
          />
          <span v-if="headerAvatarOverflow > 0" class="sh-avatar-overflow">
            +{{ headerAvatarOverflow }}
          </span>
        </div>
        <div class="sh-messaging-header-info">
          <span class="sh-messaging-recipient-names">
            {{ headerTitle }}
          </span>
          <span v-if="headerRallyName" class="sh-messaging-from">
            from
            <img :src="headerRallyIconPath" class="sh-messaging-rally-icon" alt="" aria-hidden="true" />
            <span class="sh-messaging-rally-name">{{ headerRallyName }}</span>
          </span>
        </div>
        <div class="sh-messaging-header-menu" v-click-outside="closeHeaderMenu">
          <span
            class="sh-messaging-header-menu-btn"
            aria-label="Conversation options"
            @click.stop="headerMenuOpen = !headerMenuOpen"
          >
            <i class="fal fa-ellipsis-h" aria-hidden="true"></i>
          </span>
          <div v-if="headerMenuOpen" class="sh-msg-menu-dropdown sh-header-menu-dropdown">
            <div class="sh-msg-menu-item" @click="openMembersModal">
              <i class="fal fa-users" aria-hidden="true"></i> View All Members
            </div>
            <div class="sh-msg-menu-item" @click="deleteConversation">
              <i class="fal fa-trash-alt" aria-hidden="true"></i> Delete Conversation
            </div>
            <div v-if="directMessageUserId && !directUserBlocked" class="sh-msg-menu-item" @click="blockDirectUser">
              <i class="fal fa-user-slash" aria-hidden="true"></i> Block User
            </div>
            <div v-if="directMessageUserId && directUserBlocked" class="sh-msg-menu-item" @click="unblockDirectUser">
              <i class="fal fa-user-check" aria-hidden="true"></i> Unblock User
            </div>
          </div>
        </div>
      </header>

      <div class="sh-messaging-thread" v-if="!activeThreadId">
        <MessagingEmptyState />
      </div>

      <div class="sh-messaging-thread" v-if="activeThreadId" ref="threadScroll">
        <template v-for="(content, idx) in selectedContents" :key="content.id">
          <div v-if="needsDateSeparator(idx)" class="sh-messaging-date-separator">
            {{ dateLabel(content) }}
          </div>
          <MessageBubble
            :ref="`${content.id}`"
            :content="content"
            :observer="observer"
          />
        </template>
      </div>

      <footer
        v-if="activeThread"
        class="sh-messaging-footer"
      >
        <div v-if="directUserBlocked" class="sh-messaging-blocked-footer">
          You have blocked this user.
          <a href="#" class="sh-messaging-blocked-link" @click.prevent="unblockDirectUser">Unblock</a>
          to send a new message.
        </div>
        <div v-if="!directUserBlocked" class="sh-messaging-privacy">
          Who can see this conversation? Only you and
          <span class="sh-messaging-privacy-name">
            {{ participantNames }}
          </span>
        </div>
        <div v-if="!directUserBlocked" class="sh-messaging-input-row">
          <input
            class="sh-messaging-input"
            type="text"
            v-model="dmText"
            placeholder="Create a message..."
            @keyup.enter="sendMessage"
          />
          <button
            type="button"
            class="sh-messaging-attach-btn"
            aria-label="Send message"
            @click.prevent="sendMessage"
          >
            <i class="fal fa-paper-plane" aria-hidden="true"></i>
          </button>
        </div>
      </footer>
    </section>

    <BlockUserConfirm :visible="blockConfirmOpen" :mode="blockConfirmMode" @confirm="confirmBlockDirectUser" @cancel="blockConfirmOpen = false" />
    <BlockToast />

    <div
      v-if="membersModalOpen"
      class="sh-members-modal-overlay"
      @click.self="closeMembersModal"
    >
      <div class="sh-members-modal">
        <span class="sh-members-modal-close" @click="closeMembersModal">
          <i class="fal fa-times" aria-hidden="true"></i>
        </span>

        <div class="sh-members-modal-cluster sh-modal-cluster">
          <UserAvatar
            v-for="uid in modalClusterUserIds"
            :key="uid"
            :pUserId="uid"
            pSize="40px"
            p-line-height="40px"
            p-font-size="0.9rem"
            type="author-avatar"
          />
          <span v-if="modalOverflowCount > 0" class="sh-modal-cluster-overflow">
            +{{ modalOverflowCount }}
          </span>
        </div>

        <h3 class="sh-members-modal-title">
          {{ membersModalTitle }}
        </h3>
        <a
          v-if="activeThread && activeThread.isGroup"
          class="sh-members-modal-rename-link"
          href="#"
          @click.prevent="openRenameModal"
        >Change Name</a>

        <div v-if="headerRallyName" class="sh-members-modal-rally">
          <img :src="headerRallyIconPath" alt="" aria-hidden="true" />
          <span class="sh-members-modal-rally-name">{{ headerRallyName }}</span>
          <span class="sh-members-modal-count">({{ allThreadMembers.length }} members)</span>
        </div>

        <div class="sh-members-modal-list">
          <div
            v-for="m in allThreadMembers"
            :key="m.uid"
            class="sh-members-modal-row"
          >
            <UserAvatar
              :pUserId="m.uid"
              pSize="48px"
              p-line-height="48px"
              p-font-size="1.1rem"
              type="author-avatar"
            />
            <span class="sh-members-modal-name">{{ memberDisplayName(m) }}</span>
            <span
              v-if="canRemoveMember(m)"
              class="sh-members-modal-icon-btn"
              :title="isMe(m) ? 'Leave conversation' : 'Remove from conversation'"
              @click="removeMember(m)"
            >
              <i :class="['fal', isMe(m) ? 'fa-sign-out' : 'fa-user-minus']" aria-hidden="true"></i>
            </span>
            <button
              class="sh-members-modal-profile-btn"
              type="button"
              @click="viewProfile(m)"
            >View Profile</button>
          </div>
        </div>

        <div v-if="activeThread && activeThread.isGroup" class="sh-members-modal-footer">
          <span v-if="!addMemberOpen" class="sh-members-modal-add" @click.stop="openAddMember">
            <span class="sh-members-modal-add-plus">
              <i class="fal fa-plus" aria-hidden="true"></i>
            </span>
            Add Member
          </span>
          <template v-else>
            <input
              ref="addMemberInput"
              v-model="addMemberQuery"
              class="sh-members-modal-add-input"
              type="text"
              placeholder="Search members..."
              @input="onAddMemberQueryChange"
            />
            <div v-if="addMemberResults.length > 0" class="sh-members-modal-add-results">
              <div
                v-for="member in addMemberResults"
                :key="member.id"
                class="sh-members-modal-add-result"
                @click="addMemberToThread(member)"
              >
                {{ member.attributes['first-name'] }} {{ member.attributes['last-name'] }}
              </div>
            </div>
          </template>
        </div>
      </div>
    </div>

    <div
      v-if="renameModalOpen"
      class="sh-members-modal-overlay"
      @click.self="renameModalOpen = false"
    >
      <div class="sh-members-modal sh-rename-modal">
        <span class="sh-members-modal-close" @click="renameModalOpen = false">
          <i class="fal fa-times" aria-hidden="true"></i>
        </span>
        <div class="sh-members-modal-cluster sh-modal-cluster">
          <UserAvatar
            v-for="uid in modalClusterUserIds"
            :key="uid"
            :pUserId="uid"
            pSize="40px"
            p-line-height="40px"
            p-font-size="0.9rem"
            type="author-avatar"
          />
          <span v-if="modalOverflowCount > 0" class="sh-modal-cluster-overflow">
            +{{ modalOverflowCount }}
          </span>
        </div>
        <input
          class="sh-rename-input"
          v-model="renameText"
          type="text"
          placeholder="Group Name"
          @keyup.enter="saveGroupName"
        />
        <button
          class="sh-rename-save-btn"
          type="button"
          :disabled="!renameText.trim()"
          @click="saveGroupName"
        >Save Changes</button>
      </div>
    </div>
  </div>
</template>

<script>
  import { mapState, mapActions, mapGetters } from "vuex"
  import { userInfo } from "../common/user-info";
  import { compareBy } from "../common";
  import moment from "moment";
  import MessagingEmptyState from '@/components/MessagingEmptyState.vue'
  import SubchannelsMenu from "../components/SubchannelsMenu.vue"
  import NewMessageComposer from "../components/NewMessageComposer.vue"
  import MessageBubble from "../components/MessageBubble.vue"
  import BlockUserConfirm from "../components/BlockUserConfirm.vue"
  import BlockToast from "../components/BlockToast.vue"
  import UserAvatar from "../components/UserAvatar.vue"
  import clickOutside from "../directives/v-click-outside.js";
  import { eventBus } from "../main.js";
  import '@/stylesheets/sh-messaging.css';
  export default {
    components: {
      MessagingEmptyState,
      SubchannelsMenu,
      NewMessageComposer,
      MessageBubble,
      BlockUserConfirm,
      BlockToast,
      UserAvatar
    },
    // The global click-outside registration is commented out in main.js, so
    // register the directive locally (same as MessageBubble).
    directives: { "click-outside": clickOutside },
    data() {
      return {
        activeThreadId: null,
        composerOpen: false,
        headerMenuOpen: false,
        membersModalOpen: false,
        blockConfirmOpen: false,
        blockConfirmMode: "block",
        renameModalOpen: false,
        renameText: "",
        addMemberOpen: false,
        addMemberQuery: "",
        addMemberSearchDebounce: null,
        subchannelsState: true,
        loadAttempted: false,
        observer: null,
        dmText: "",
        threadSelected: false,
        // Near-real-time updates with adaptive cadence: poll fast while the
        // conversation is active, back off geometrically while nothing is
        // happening, and snap back to fast on any user action or new message.
        // Thread list + unread dots refresh roughly every 30s regardless.
        pollTimer: null,
        pollInFlight: false,
        pollIdleTicks: 0,
        pollDelayMs: 5000,
        pollLastListRefresh: 0,
        visibilityHandler: null
      };
    },
    computed: {
      ...mapState("channels", ["channelsById", "threadsById"]),
      ...mapGetters("channels", ["threads"]),
      ...mapGetters("blockedUsers", ["isUserBlocked"]),
      // The other person in a 1:1 conversation (participantUserIds excludes
      // "me"); null for group chats, which have no single user to block.
      directMessageUserId() {
        if (!this.activeThread || this.activeThread.isGroup) { return null; }
        const ids = this.activeThread.participantUserIds || [];
        return ids.length === 1 ? ids[0] : null;
      },
      directUserBlocked() {
        return this.directMessageUserId != null && this.isUserBlocked(this.directMessageUserId);
      },
      ...mapState("contents", ["allContentByContextTypeAndId", "contentById"]),
      ...mapState("user", ["status", "user", "usersById"]),
      ...mapState("rallies", ["allRalliesByRallyId", "rallyChannelsByRallyId"]),
      ...mapState("memberships", ["memberSearchResults"]),
      activeThread(){
        return this.activeThreadId ? this.threadsById[this.activeThreadId + ""] : null
      },
      participantUsers(){
        if (!this.activeThread) { return [] }
        const me = userInfo().user_id + ""
        return this.activeThread.participantUserIds
          .filter(uid => (uid + "") !== me) // never show "you" in your own participant list
          .map(uid => this.usersById[uid])
          .filter(u => u && u.attributes)
      },
      participantNames(){
        return this.participantUsers
          .map(u => `${u.attributes['first-name']} ${u.attributes['last-name']}`)
          .join(', ')
      },
      // Same cluster rules as the sidebar rows: exactly 3 members -> three
      // circles, more than 3 -> two circles plus the "+N" badge.
      headerAvatarUserIds(){
        if (!this.activeThread) { return [] }
        const ids = this.activeThread.participantUserIds
        return ids.length === 3 ? ids.slice(0, 3) : ids.slice(0, 2)
      },
      headerAvatarOverflow(){
        if (!this.activeThread) { return 0 }
        const n = this.activeThread.participantUserIds.length
        return n > 3 ? n - 2 : 0
      },
      selectedContents(){
        if (!this.activeThreadId) { return [] }
        const cs = this.allContentByContextTypeAndId[this.activeThreadId] || []
        return cs && cs.sort ? [...cs].sort(compareBy({}, "created-at")) : cs
      },
      isMobileSize() {
        return document.body.scrollWidth <= 769;
      },
      headerRallyName(){
        if (!this.activeThread) { return null }
        const r = this.allRalliesByRallyId && this.allRalliesByRallyId[this.activeThread.rallyId]
        const fromRallies = r?.rally?.attributes?.title || r?.attributes?.title
        if (fromRallies) { return fromRallies }
        // Fallback: rally name off the channel payload when present.
        const ch = this.activeThread.channel
        return (ch && ch.attributes && (ch.attributes['rally-name'] || ch.attributes['rally-title'])) || null
      },
      headerRallyIconPath(){
        // Hub vs rally context icon, keyed off the rally payload's is_hub
        // (dasherized to 'is-hub' by the JSON:API layer). Unknown -> rally.
        const r = this.activeThread && this.allRalliesByRallyId && this.allRalliesByRallyId[this.activeThread.rallyId]
        const isHub = r?.rally?.attributes?.['is-hub'] || r?.attributes?.['is-hub']
        return isHub ? '/icons/hubicon.svg' : '/icons/rallyicon.svg'
      },
      // Every member of the conversation, including the current user (the
      // thread's participantUserIds excludes "me" by construction).
      allThreadMembers(){
        if (!this.activeThread) { return [] }
        const me = userInfo().user_id + ""
        const ids = this.activeThread.participantUserIds.map(uid => uid + "")
        if (!ids.includes(me)) { ids.push(me) }
        return ids.map(uid => ({ uid, user: this.usersById[uid] }))
      },
      // Header title: a custom group name when one has been set, otherwise
      // the participant names.
      headerTitle(){
        return this.customThreadName(this.activeThread) || this.participantNames
      },
      // A renamed group's modal is titled with its custom name; otherwise
      // the generic label.
      membersModalTitle(){
        const custom = this.customThreadName(this.activeThread)
        if (custom) { return custom }
        return this.activeThread && this.activeThread.isGroup ? 'Group Chat Members' : 'Members'
      },
      // Modal cluster mirrors the sidebar cluster: first two participants,
      // "+N" for everyone else (excluding me, same as the sidebar rows).
      modalClusterUserIds(){
        if (!this.activeThread) { return [] }
        return this.activeThread.participantUserIds.slice(0, 2)
      },
      modalOverflowCount(){
        if (!this.activeThread) { return 0 }
        return Math.max(0, this.activeThread.participantUserIds.length - 2)
      },
      isThreadCreator(){
        const creatorId = this.activeThread?.channel?.attributes?.['creator-id']
        return creatorId != null && (creatorId + "") === (userInfo().user_id + "")
      },
      addMemberResults(){
        const existing = new Set(this.allThreadMembers.map(m => m.uid))
        const results = Array.isArray(this.memberSearchResults) ? this.memberSearchResults : []
        return results.filter(m => {
          const uid = m.attributes && (m.attributes['user-id'] + "")
          // Active rally members only, minus people already in the thread.
          return uid && !existing.has(uid) && m.attributes.status === 'Active'
        })
      }
    },
    async mounted() {
      this.subchannelsState = true;
      this.threadSelected = false;
      await this.clearNewPrivateMessages()
      await this.loadPageData()
      try {
        for (const t of this.threads) {
          this.loadThread(t.id);
        }
      } catch (e){
        console.log(e)
      }
      this.startPolling();
    },
    beforeUnmount() {
      this.stopPolling();
    },
    async created(){
      // Load the current user's blocks once so bubbles can render the blocked
      // state. Safe if the rcs endpoint isn't deployed yet — the store
      // swallows the error and reports nobody as blocked.
      this.fetchBlockedUsers();
      this.observer = new IntersectionObserver(
          this.onElementObserved,
          {
            root: this.$el,
            threshold: 1.0,
          }
      );
      eventBus.on('selectSubchannel', (data) => {
        // Legacy event: payload is a recipient user id (fired by avatar/DM
        // entry points). Resolve it to the 1:1 thread.
        this.selectThreadForUser(data);
      })
      eventBus.on('selectThread', (threadId) => {
        this.selectThread(threadId);
      })
    },
    methods: {
      ...mapActions("channels", ["fetchUserThreads", "clearNewPrivateMessages", "deleteThread", "removeChannelUser", "renameThread", "addChannelUsers", "fetchUnreadCounts", "markThreadRead"]),
      ...mapActions("memberships", ["searchMembers"]),
      ...mapActions("contents", ["fetchContents", "fetchContentById", "createContents"]),
      ...mapActions("activities", ["contentViewed"]),
      ...mapActions("blockedUsers", ["fetchBlockedUsers", "blockUser", "unblockUser"]),
      // Block the other party of a 1:1 thread from the header menu. For
      // context, pass their most recent message in this thread (if any) as
      // content_uid so the Slack alert shows what prompted it.
      blockDirectUser() {
        this.headerMenuOpen = false;
        if (this.directMessageUserId == null) { return; }
        this.blockConfirmMode = "block";
        this.blockConfirmOpen = true;
      },
      unblockDirectUser() {
        this.headerMenuOpen = false;
        if (this.directMessageUserId == null) { return; }
        this.blockConfirmMode = "unblock";
        this.blockConfirmOpen = true;
      },
      confirmBlockDirectUser() {
        this.blockConfirmOpen = false;
        const userId = this.directMessageUserId;
        if (userId == null) { return; }
        if (this.blockConfirmMode === "unblock") {
          this.unblockUser({ userId });
          return;
        }
        const theirs = [...this.selectedContents].reverse()
          .find(c => parseInt(c.attributes?.["creator-id"]) === parseInt(userId));
        this.blockUser({ userId, contentUid: theirs ? theirs.id : undefined });
      },
      viewBlockedUsers() {
        // Blocked Users lives in the User Settings accordion; ?open=blocked
        // tells UserSettings to expand that tab on arrival.
        this.$router.push({ path: '/settings', query: { open: 'blocked' } });
      },
      scrollToContentId(contentId) {
        this.$nextTick(() => {
          this.$refs[contentId][0].$el.scrollIntoView({ behavior: 'smooth' });});
      },
      sendMessage() {
        if (!this.activeThread || !this.dmText) { return; }
        // Sending means the conversation is live — poll fast for replies.
        this.resetPollCadence();
        var params = {
          contentType: 'Message',
          contextId: this.activeThread.id,
          contextType: "Channel",
          // Content type stays PrivateMessage for both 1:1 and group threads;
          // the notification pipeline (rns/runs) keys on it.
          type: 'PrivateMessage',
          contextChannelId: this.activeThread.id,
          contextRallyId: this.activeThread.rallyId,
          status: 1,
          body: this.dmText
        };
        this.createContents(params);
        this.dmText = ""
        this.scrollThreadToBottom();
      },
      async loadPageData() {
          await this.fetchUserThreads({userId: userInfo().user_id})
          this.loadAttempted = true
          // Unread dots (thread rows + top-nav envelope). Not awaited —
          // rendering the list shouldn't wait on the counts.
          this.fetchUnreadCounts({ zeroChannelId: this.activeThreadId })
      },
      onElementObserved(entries) {
        entries.forEach(({ target, isIntersecting}) => {
          if (!isIntersecting) {
            return;
          }
          let contentId = target?.attributes?.contentid?.value;
          let contentType = target?.attributes?.contenttype?.value;
          if (contentId && contentType){
            let params = {}
            params['contentId'] = contentId
            params['contentType'] = contentType
            params['actionType'] = 'viewed'
            params['rallyId'] = this.activeThread ? this.activeThread.rallyId : null
            params['userId'] = userInfo()['id']
            this.contentViewed(params);
          }
        })},
      perform(method, params) {
        // Execute whatever method name came from a child component.
        params ? this[method](params) : this[method]();
      },
      async selectThread(threadId) {
        this.composerOpen = false;
        this.activeThreadId = threadId + "";
        // Switching threads resets the cadence so the new thread stays fresh.
        this.resetPollCadence();
        // Opening a thread marks the whole conversation read: green row dot
        // clears immediately (optimistic), and the top-nav envelope dot
        // clears once no other thread has unread messages. Fire-and-forget —
        // opening the thread shouldn't wait on it.
        this.markThreadRead({ threadId: this.activeThreadId });
        await this.loadThread(threadId);
        if (this.isMobileSize) {
          this.subchannelsState = false;
        }
        const contentIdFromUrl = this.$route.query.cid
        if(contentIdFromUrl) {
          this.scrollToContentId(contentIdFromUrl);
        } else {
          this.scrollThreadToBottom();
        }
      },
      // Resolves a recipient user id (legacy deep links / avatar entry
      // points) to that user's 1:1 thread.
      selectThreadForUser(recipientUserId) {
        const t = this.threads.find(t =>
          !t.isGroup && t.participantUserIds.some(uid => parseInt(uid) === parseInt(recipientUserId)))
        if (t) {
          this.selectThread(t.id)
        }
      },
      async loadThread(threadId) {
        const thread = this.threadsById[threadId + ""]
        if (!thread) { return; }
        var params = {
          contextType: "Channel",
          contextChannelId: thread.id,
          contextId: thread.id,
          contentType: 'Message',
          include: ["shares", "attachments"],
          userId: "*",
          privateMessageChannel: thread.channel,
          ignorePagination: true
        };
        await this.fetchContents(params);
      },
      toggleSubchannels() {
        // Collapse/expand the sidebar into an avatar rail — do not navigate;
        // the open thread stays open.
        this.subchannelsState = !this.subchannelsState;
      },
      needsDateSeparator(idx) {
        const curr = this.selectedContents[idx];
        if (!curr) { return false; }
        if (idx === 0) { return true; }
        const prev = this.selectedContents[idx - 1];
        return !moment(curr.attributes['created-at']).isSame(prev.attributes['created-at'], 'day');
      },
      dateLabel(content) {
        return moment(content.attributes['created-at']).format('MMM D, YYYY');
      },
      isNearBottom() {
        const el = this.$refs.threadScroll;
        if (!el) { return true; }
        return el.scrollHeight - el.scrollTop - el.clientHeight < 150;
      },
      scrollThreadToBottom() {
        this.$nextTick(() => {
          const el = this.$refs.threadScroll;
          if (el) { el.scrollTop = el.scrollHeight; }
        });
      },
      // Tunables. POLL_FAST_MS is the cadence while messages are flowing;
      // after POLL_IDLE_TICKS_BEFORE_BACKOFF quiet ticks the delay doubles
      // each tick up to POLL_SLOW_MS. Thread list / unread dots refresh at
      // least every LIST_REFRESH_MS. Overridable at runtime via
      // window.RH_MESSAGING_POLL = { fastMs, slowMs, idleTicks, listMs } for
      // tuning in the field without a build.
      pollConfig() {
        const o = (typeof window !== 'undefined' && window.RH_MESSAGING_POLL) || {};
        return {
          fastMs:    o.fastMs    || 5000,
          slowMs:    o.slowMs    || 30000,
          idleTicks: o.idleTicks || 6,      // ~30s of quiet before backing off
          listMs:    o.listMs    || 30000
        };
      },
      startPolling() {
        if (this.pollTimer) { return; }
        this.pollDelayMs = this.pollConfig().fastMs;
        this.pollIdleTicks = 0;
        this.scheduleNextPoll();
        // Background tabs skip ticks (see pollForUpdates); refresh
        // immediately — and at fast cadence — when the tab becomes visible
        // again so the thread catches up without waiting for the next tick.
        this.visibilityHandler = () => {
          if (document.visibilityState === 'visible') {
            this.pollNow();
          }
        };
        document.addEventListener('visibilitychange', this.visibilityHandler);
      },
      stopPolling() {
        if (this.pollTimer) {
          clearTimeout(this.pollTimer);
          this.pollTimer = null;
        }
        if (this.visibilityHandler) {
          document.removeEventListener('visibilitychange', this.visibilityHandler);
          this.visibilityHandler = null;
        }
      },
      scheduleNextPoll() {
        if (this.pollTimer) { clearTimeout(this.pollTimer); }
        this.pollTimer = setTimeout(async () => {
          this.pollTimer = null;
          await this.pollForUpdates();
          // Only keep scheduling while polling is still "on" (stopPolling
          // sets visibilityHandler to null).
          if (this.visibilityHandler) { this.scheduleNextPoll(); }
        }, this.pollDelayMs);
      },
      // Reset to the fast cadence and poll immediately. Called on user
      // actions (send, thread switch, tab focus) and when new messages land.
      pollNow() {
        this.pollDelayMs = this.pollConfig().fastMs;
        this.pollIdleTicks = 0;
        if (!this.visibilityHandler) { return; }   // polling stopped
        if (this.pollTimer) { clearTimeout(this.pollTimer); this.pollTimer = null; }
        this.pollForUpdates().finally(() => {
          if (this.visibilityHandler) { this.scheduleNextPoll(); }
        });
      },
      // Snap back to the fast cadence WITHOUT polling immediately, and
      // reschedule any pending (possibly long) tick so the next poll really
      // is fastMs away. Used by user actions whose own request already
      // refreshes the thread (send, thread switch).
      resetPollCadence() {
        this.pollDelayMs = this.pollConfig().fastMs;
        this.pollIdleTicks = 0;
        if (this.visibilityHandler && this.pollTimer) { this.scheduleNextPoll(); }
      },
      // Called after each tick with whether anything changed; drives backoff.
      adaptPollDelay(sawActivity) {
        const cfg = this.pollConfig();
        if (sawActivity) {
          this.pollIdleTicks = 0;
          this.pollDelayMs = cfg.fastMs;
          return;
        }
        this.pollIdleTicks += 1;
        if (this.pollIdleTicks >= cfg.idleTicks) {
          this.pollDelayMs = Math.min(this.pollDelayMs * 2, cfg.slowMs);
        }
      },
      async pollForUpdates() {
        // Don't poll from background tabs, and never stack a new request on
        // top of a slow one.
        if (document.visibilityState === 'hidden') { return; }
        if (this.pollInFlight) { return; }
        this.pollInFlight = true;
        let sawActivity = false;
        try {
          // Every tick: refresh the open thread's messages (store merge
          // dedupes, so this is an incremental update from the UI's view).
          // New messages = activity, which keeps the cadence fast.
          if (this.activeThreadId) {
            const before = this.selectedContents.length;
            await this.loadThread(this.activeThreadId);
            sawActivity = this.selectedContents.length !== before;
          }
          // Time-based (not tick-based, since tick length now varies):
          // refresh the thread list and unread dots at least every listMs so
          // threads started by other people appear in the sidebar.
          const now = Date.now();
          if (now - this.pollLastListRefresh >= this.pollConfig().listMs) {
            this.pollLastListRefresh = now;
            await this.fetchUserThreads({userId: userInfo().user_id});
            // The open thread is forced to zero: the user is looking at it,
            // and the server-side 'viewed' writes are async (Resque) so a raw
            // fetch could transiently re-flag it.
            await this.fetchUnreadCounts({ zeroChannelId: this.activeThreadId });
          }
        } catch (e) {
          // A failed tick is fine — the next one retries.
          console.log("poll failed", e)
        } finally {
          this.pollInFlight = false;
          this.adaptPollDelay(sawActivity);
        }
      },
      onNewMessage() {
        this.composerOpen = true;
        this.activeThreadId = null;
      },
      closeHeaderMenu() {
        this.headerMenuOpen = false;
      },
      // Mobile: chevron in the header returns to the thread list (the
      // sidebar is hidden while a thread is open on small screens).
      backToList() {
        this.activeThreadId = null;
        this.subchannelsState = true;
      },
      openMembersModal() {
        this.headerMenuOpen = false;
        this.membersModalOpen = true;
        this.addMemberOpen = false;
        this.addMemberQuery = "";
      },
      closeMembersModal() {
        this.membersModalOpen = false;
        this.addMemberOpen = false;
        this.addMemberQuery = "";
      },
      // A channel name is a display name only when it isn't the service's
      // auto-generated "GroupMessage: <ids>" / "PrivateMessage: <ids>" shape.
      customThreadName(thread) {
        const n = thread?.channel?.attributes?.name;
        if (!n || n.startsWith('GroupMessage:') || n.startsWith('PrivateMessage:')) { return null; }
        return n;
      },
      memberDisplayName(m) {
        const isMe = m.uid === (userInfo().user_id + "");
        if (!m.user || !m.user.attributes) { return isMe ? "You" : "…"; }
        const name = `${m.user.attributes['first-name']} ${m.user.attributes['last-name']}`;
        return isMe ? `${name} (you)` : name;
      },
      isMe(m) {
        return m.uid === (userInfo().user_id + "");
      },
      // Leave is available to any member of a group; removing someone else
      // is creator-only (matches ChannelUserAuthorizer#deletable_by?).
      canRemoveMember(m) {
        if (!this.activeThread || !this.activeThread.isGroup) { return false; }
        return this.isMe(m) || this.isThreadCreator;
      },
      async removeMember(m) {
        const leaving = this.isMe(m);
        const prompt = leaving
          ? "Leave this conversation? You will no longer see its messages."
          : `Remove ${this.memberDisplayName(m)} from this conversation?`;
        if (!window.confirm(prompt)) { return; }
        try {
          await this.removeChannelUser({ thread: this.activeThread, userId: m.uid });
          if (leaving) {
            this.closeMembersModal();
            this.activeThreadId = null;
          }
        } catch (e) {
          console.log("removeChannelUser failed", e);
        }
      },
      // Profiles live on the rally's roster channel — there is no dedicated
      // profile page in this app. The roster channel differs per rally
      // (type 'Directory' for Member Directory rallies, 'Members' for
      // Friends), each with its own slug, so resolve the real channel
      // instead of hardcoding a slug.
      async viewProfile(m) {
        if (!this.activeThread) { return; }
        const rallyId = this.activeThread.rallyId;
        this.closeMembersModal();
        try {
          // Cached/deduped in the store; cheap when already loaded.
          await this.$store.dispatch("rallies/fetchRalliesChannels", { rallyId });
        } catch (e) {
          console.log("fetchRalliesChannels failed", e);
        }
        const chans = this.rallyChannelsByRallyId?.[rallyId] || [];
        const rosterChannel =
          chans.find(c => c?.attributes?.type === 'Directory') ||
          chans.find(c => c?.attributes?.type === 'Members');
        const slug = rosterChannel?.attributes?.slug || 'members';
        this.$router.push(`/${rallyId}/${slug}`);
      },
      openRenameModal() {
        this.renameText = this.customThreadName(this.activeThread) || "";
        this.membersModalOpen = false;
        this.renameModalOpen = true;
      },
      async saveGroupName() {
        const name = this.renameText.trim();
        if (!name || !this.activeThread) { return; }
        try {
          await this.renameThread({ thread: this.activeThread, name });
          this.renameModalOpen = false;
          this.membersModalOpen = true;
        } catch (e) {
          console.log("renameThread failed", e);
        }
      },
      openAddMember() {
        this.addMemberOpen = true;
        this.addMemberQuery = "";
        this.$store.commit("memberships/setMemberSearchResults", { members: [] });
        this.$nextTick(() => this.$refs.addMemberInput?.focus());
      },
      onAddMemberQueryChange() {
        clearTimeout(this.addMemberSearchDebounce);
        const query = this.addMemberQuery;
        if (!query || query.length < 2) { return; }
        this.addMemberSearchDebounce = setTimeout(() => {
          this.searchMembers({ query, rallyId: this.activeThread.rallyId });
        }, 300);
      },
      async addMemberToThread(member) {
        const uid = member.attributes["user-id"] + "";
        try {
          await this.addChannelUsers({ channelId: this.activeThreadId, userIds: [uid] });
          this.addMemberOpen = false;
          this.addMemberQuery = "";
          // Refresh the roster (and hydrate the new member's name/avatar).
          await this.fetchUserThreads({ userId: userInfo().user_id });
        } catch (e) {
          console.log("addChannelUsers failed", e);
        }
      },
      async deleteConversation() {
        this.headerMenuOpen = false;
        if (!this.activeThreadId) { return; }
        // Deleting removes the conversation for every member, so confirm.
        if (!window.confirm("Delete this conversation for all members? This cannot be undone.")) {
          return;
        }
        try {
          await this.deleteThread({ threadId: this.activeThreadId });
          this.activeThreadId = null;
        } catch (e) {
          console.log("deleteThread failed", e);
        }
      },
      async onThreadCreated(threadId) {
        this.composerOpen = false;
        // Refresh the thread list so the new thread appears in the sidebar,
        // then open it.
        await this.fetchUserThreads({userId: userInfo().user_id})
        this.selectThread(threadId);
      },
    },
    watch: {
      // Chat behavior: when new messages arrive (e.g. via polling) and the
      // user is already reading the latest, stick to the bottom. If they've
      // scrolled up, don't yank them down.
      selectedContents(newVal, oldVal) {
        if ((newVal?.length || 0) > (oldVal?.length || 0) && this.isNearBottom()) {
          this.scrollThreadToBottom();
        }
      }
    },
    updated(){
      // Route param is a thread (channel) id; fall back to treating it as a
      // legacy recipient user id (old notification deep links).
      if (this.$route.params.activeSubchannelId && !this.threadSelected && this.threads.length > 0) {
        const param = this.$route.params.activeSubchannelId + ""
        if (this.threadsById[param]) {
          this.selectThread(param);
        } else {
          this.selectThreadForUser(param);
        }
        this.subchannelsState = false;
        this.threadSelected = true;
      }
    },
  };
</script>
